import {
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { LayoutGridIcon, ListIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LIBRARY_VIEW_MODE, type LibraryViewMode } from '../model/useLibraryViewMode';

/**
 * Which of the two ways the library is drawn.
 *
 * Icons alone: the pair sits above the assets it changes, so the two pictures say more in less room
 * than the words would, and each carries its name for anything that cannot see them.
 */
export const LibraryViewToggle = ({
  value,
  onChange,
}: {
  value: LibraryViewMode;
  onChange: (next: LibraryViewMode) => void;
}) => {
  const { t } = useTranslation('tools');

  const tab = (mode: LibraryViewMode, label: string, icon: ReactNode) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <TabsTrigger
          value={mode}
          aria-label={label}
          // Both triggers write `data-state`, and the tooltip's would arrive as a prop on this one
          // and win, leaving the chosen tab styled as though it were not chosen. Stating it here
          // puts it back: props set on the child of an `asChild` outrank the ones handed down.
          data-state={value === mode ? 'active' : 'inactive'}
        >
          {icon}
        </TabsTrigger>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );

  return (
    // Its own provider rather than the app's: this is shared code, mounted by more than one
    // host, and a tooltip whose provider is missing throws rather than degrading.
    <TooltipProvider>
      <Tabs
        value={value}
        onValueChange={(next) => onChange(next as LibraryViewMode)}
        data-testid="brand-media-view-toggle"
      >
        <TabsList>
          {tab(
            LIBRARY_VIEW_MODE.list,
            t('brandMedia.viewToggle.listView'),
            <ListIcon className="size-4" />,
          )}
          {tab(
            LIBRARY_VIEW_MODE.cards,
            t('brandMedia.viewToggle.cardsView'),
            <LayoutGridIcon className="size-4" />,
          )}
        </TabsList>
      </Tabs>
    </TooltipProvider>
  );
};
